import React, { useRef, useCallback, useMemo } from 'react';
import { IMaskInput } from 'react-imask';
import { Input, InputAdornment, TextField } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * IMask adapter component for phone number input
 * Handles the formatting for display purposes
 */
export const PhoneInputMask = React.forwardRef(function PhoneInputMask(props, ref) {
  const { onChange, mask, ...other } = props;
  const imaskRef = useRef();

  /**
   * Handles input value changes and updates Formik
   */
  const handleAccept = useCallback((value, mask) => {
    // Get only the numeric digits (unmasked value)
    const unmaskedValue = mask.unmaskedValue;

    // Update Formik field with clean numeric value
    onChange({
      target: {
        name: props.name,
        value: unmaskedValue
      }
    });
  }, [onChange, props.name]);

  return (
    <IMaskInput
      {...other}
      ref={imaskRef}
      mask={mask}
      definitions={{
        '#': /[0-9]/ // Only allow digits
      }}
      inputRef={ref}
      onAccept={handleAccept}
      unmask={true} // Always work with unmasked values
      lazy={false} // Show placeholder immediately
    />
  );
});

PhoneInputMask.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  mask: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/**
 * PhoneNumberField component for Formik forms
 *
 * Features:
 * - Automatic formatting based on business preferences
 * - Clean integration with Material-UI and Formik
 * - Server handles E.164 conversion for database storage
 * - Client receives national format numbers from server
 *
 * @param {Object} field - Formik field props
 * @param {Object} form - Formik form props
 * @param {string} mask - Phone number format mask (e.g., "(###) ###-####")
 * @param {string} countryCode - Country code for display and E.164 conversion
 * @param {Object} props - Additional TextField props
 */
const PhoneNumberField = ({ field, form, mask, countryCode, ...props }) => {
  const { name, value } = field;
  const { setFieldValue } = form;

  /**
   * Enhanced change handler for Formik integration
   */
  const handleChange = useCallback((event) => {
    setFieldValue(name, event.target.value);
  }, [setFieldValue, name]);

  /**
   * Memoized input props to prevent unnecessary re-renders
   */
  const inputProps = useMemo(() => ({
    mask,
    value,
  }), [mask, value]);

  return (
    <TextField
      {...props}
      name={name}
      value={value || ''}
      onChange={handleChange}
      InputProps={{
        startAdornment: <InputAdornment position="start">{countryCode}</InputAdornment>,
        inputComponent: PhoneInputMask,
        inputProps,
        ...props.InputProps,
      }}
    />
  );
};

PhoneNumberField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  mask: PropTypes.string.isRequired,
  countryCode: PropTypes.string,
  InputProps: PropTypes.object,
};

PhoneNumberField.defaultProps = {
  countryCode: null,
  InputProps: {},
};

export default PhoneNumberField;
